/**
 * Runtime configuration from GET /api/config (public, same-origin). It carries the
 * environment fields plus the white-label brand pack (docs/brand-config.md). The UI
 * boots from it, so parsing is tolerant: a missing or malformed pack falls back to
 * a neutral default instead of blocking the app.
 */
import { z } from 'zod';

const cssVarMap = z.record(z.string(), z.string());

const brandSchema = z.object({
  id: z.string(),
  productName: z.string(),
  companyName: z.string(),
  shortName: z.string().optional(),
  documentTitle: z.string(),
  faviconUrl: z.string(),
  logoUrl: z.string(),
  /** Dark-mode logo; `logoUrl` is used when it's absent. */
  logoDarkUrl: z.string().optional(),
  logoAlt: z.string().optional(),
  loginHeroUrl: z.string().optional(),
  appleTouchIconUrl: z.string().optional(),
});

const themeSchema = z.object({
  fontFamily: z.string().optional(),
  radius: z.string().optional(),
  light: cssVarMap,
  dark: cssVarMap,
});

const layoutSchema = z.object({
  variant: z.enum(['sidebar-inset', 'sidebar-floating', 'top-nav', 'compact']),
  collapsible: z.enum(['icon', 'offcanvas', 'none']).optional(),
  showBreadcrumbs: z.boolean().optional(),
});

const navChildSchema = z.object({
  id: z.string(),
  label: z.string().optional(),
  hidden: z.boolean().optional(),
  order: z.number().optional(),
});

const navItemSchema = navChildSchema.extend({
  icon: z.string().optional(),
  children: z.array(navChildSchema).optional(),
});

export type BrandIdentity = z.infer<typeof brandSchema>;
export type BrandTheme = z.infer<typeof themeSchema>;
export type BrandLayout = z.infer<typeof layoutSchema>;
export type BrandNavItemOverlay = z.infer<typeof navItemSchema>;

export interface BrandPresentation {
  brand: BrandIdentity;
  theme: BrandTheme;
  layout: BrandLayout;
  navigation: { items: BrandNavItemOverlay[] };
  i18n: { pageTitles: Record<string, string> };
}

export interface RuntimeConfig {
  apiUrl: string;
  backofficeEnv: string;
  gapLauncherEnv: string;
  gcloudProject: string;
  boPrefix: string;
  boDomain: string;
  presentation: BrandPresentation;
}

export const FALLBACK_PRESENTATION: BrandPresentation = {
  brand: {
    id: 'default',
    productName: 'Backoffice',
    companyName: '',
    documentTitle: 'Backoffice',
    faviconUrl: '',
    logoUrl: '',
  },
  theme: { light: {}, dark: {} },
  layout: { variant: 'sidebar-inset', collapsible: 'icon', showBreadcrumbs: true },
  navigation: { items: [] },
  i18n: { pageTitles: {} },
};

const envSchema = z.object({
  API_URL: z.string().default('/api'),
  BACKOFFICE_ENV: z.string().default('dev'),
  GAP_LAUNCHER_ENV: z.string().default('dev'),
  GCLOUD_PROJECT: z.string().default(''),
  bo_prefix: z.string().default(''),
  bo_domain: z.string().default(''),
});

/** Parse the raw payload; each part of the brand pack falls back independently. */
export function parseRuntimeConfig(raw: unknown): RuntimeConfig {
  const obj = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
  const env = envSchema.parse(obj);
  const brand = brandSchema.safeParse(obj.brand);
  const theme = themeSchema.safeParse(obj.theme);
  const layout = layoutSchema.safeParse(obj.layout);
  const nav = z.array(navItemSchema).safeParse((obj.navigation as { items?: unknown } | undefined)?.items);
  const titles = z
    .record(z.string(), z.string())
    .safeParse((obj.i18n as { pageTitles?: unknown } | undefined)?.pageTitles);
  return {
    apiUrl: env.API_URL,
    backofficeEnv: env.BACKOFFICE_ENV.trim().toLowerCase(),
    gapLauncherEnv: env.GAP_LAUNCHER_ENV,
    gcloudProject: env.GCLOUD_PROJECT,
    boPrefix: env.bo_prefix,
    boDomain: env.bo_domain,
    presentation: {
      brand: brand.success ? brand.data : FALLBACK_PRESENTATION.brand,
      theme: theme.success ? theme.data : FALLBACK_PRESENTATION.theme,
      layout: layout.success ? layout.data : FALLBACK_PRESENTATION.layout,
      navigation: { items: nav.success ? nav.data : [] },
      i18n: { pageTitles: titles.success ? titles.data : {} },
    },
  };
}

export async function loadRuntimeConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  const res = await fetchImpl('/api/config', { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`GET /api/config failed with HTTP ${res.status}`);
  return parseRuntimeConfig(await res.json());
}
